import styled from 'styled-components';

// Section Title
export const SectionTitle = styled.h2`
  text-align: center;
  margin: var(--spacing-md) 0 var(--spacing-md) 0;
  font-size: 2.5rem;
  line-height: 1.2;
  font-weight: 300;
  color: var(--color-text);
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

export const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto var(--spacing-md) auto;
  font-size: 1rem; /* Augmenté pour une meilleure lisibilité */
  line-height: 1.4;
  color: var(--color-text-secondary);
`;

// Hero Section
export const HeroSection = styled.section`
  height: 90vh;
  min-height: 650px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  margin-top: 80px;
  padding: 0; /* Neutralise le padding global des sections pour éviter l'espace supplémentaire */
  @media (max-width: 768px) {
    margin-top: 110px; /* offset plus précis sous le header fixe sur mobile */
    height: auto; /* Laisser la section s'étendre selon le contenu sur mobile */
    min-height: initial; /* supprimer la contrainte de min-height */
    overflow: visible; /* éviter de couper le contenu si plus grand que la fenêtre */
  }
  background-color: white;
`;

export const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: -1;
`;

export const HeroContent = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  width: 100%;
  color: var(--color-text);
  position: relative;
  z-index: 2;
  
  .hero-content-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-lg);
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: var(--spacing-md);
      padding-top: 0; /* plus d'offset ici, géré par le margin-top de la section */
    }
  }
  
  .hero-text-container {
    flex: 1;
    text-align: left;
  }
  
  .hero-image-container {
    flex: 1;
    height: 500px;
    
    img.hero-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 2px;
    }
    
    @media (max-width: 768px) {
      width: 100%;
      height: auto; /* Laisser l'image définir la hauteur sur mobile */
      
      /* Sur mobile, afficher l'image en entier sans recadrage */
      img.hero-image {
        height: auto;
        object-fit: contain;
        max-height: 70vh; /* Empêche de dépasser la hauteur de l'écran */
        display: block;
        margin: 0 auto;
      }
    }
  }
`;

export const HeroTitle = styled.h1`
  font-size: 3.2rem;
  margin-bottom: var(--spacing-md);
  color: var(--color-text);
  text-align: left;
  line-height: 1.2;
  max-width: 90%;
  
  br {
    display: block;
    content: "";
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    text-align: center;
    max-width: 100%;
  }
`;

export const HeroText = styled.p`
  font-size: 1.1rem;
  margin-bottom: var(--spacing-lg);
  color: var(--color-text-secondary);
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: center;
  }
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-start;
  
  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: column;
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }
`;

// Services Section
export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
`;

export const ServiceCard = styled.div`
  background-color: white;
  padding: var(--spacing-md);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow-sm);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  height: 100%;
  min-height: 280px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--box-shadow-md);
    
    svg {
      transform: scale(1.1);
    }
  }
`;

export const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  margin-bottom: var(--spacing-md);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--color-light-gray);
  margin-top: var(--spacing-sm);
  
  svg {
    color: var(--color-dark-gray);
    transition: transform 0.3s ease;
  }
`;

export const ServiceTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: var(--spacing-sm);
  color: var(--color-text);
  font-weight: var(--font-weight-medium);
  text-align: center;
  min-height: 3.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.2;
`;

export const ServiceDescription = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
  line-height: 1.6;
  flex-grow: 1;
  text-align: center;
`;

// Projects Section
export const ProjectsContainer = styled.div`
  margin-top: var(--spacing-md);
`;

export const FeaturedProjectLayout = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const ProjectImageSection = styled.div`
  flex: 1.2;
  position: relative;
  overflow: hidden;
  margin-bottom: var(--spacing-md);
`;

export const ProjectDetailsSection = styled.div`
  flex: 0.8;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
`;

export const MainImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 400px;
  }
`;

export const MainImage = styled.img<{ $isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${props => props.$isActive ? 1 : 0};
  transition: opacity 2s ease;
`;

export const ThumbnailsContainer = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
  justify-content: center;
`;

export const Thumbnail = styled.div<{ $isActive: boolean }>`
  width: 80px;
  height: 60px;
  cursor: pointer;
  opacity: ${props => props.$isActive ? 1 : 0.5};
  border: ${props => props.$isActive ? '2px solid var(--color-accent)' : '2px solid transparent'};
  transition: opacity 0.3s ease, border 0.3s ease;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ProjectTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
  font-size: 1.7rem;
`;

export const ProjectDescription = styled.p`
  margin-bottom: var(--spacing-md);
  line-height: 1.6;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
`;

export const ProjectKeyPoints = styled.ul`
  margin-bottom: var(--spacing-md);
  padding-left: 0;
  list-style-type: none;
  
  li {
    margin-bottom: 8px;
    color: var(--color-text-secondary);
    position: relative;
    padding-left: 20px;
    
    &::before {
      content: "•";
      position: absolute;
      left: 0;
      color: var(--color-accent);
    }
  }
`;

export const ProjectKeyPoint = styled.li`
  line-height: 1.5;
`;

export const OtherProjectsHeading = styled.h3`
  margin-top: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

export const OtherProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const ProjectCard = styled.div`
  position: relative;
  height: 250px;
  overflow: hidden;
  border-radius: 4px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  &:hover .project-card-details {
    opacity: 1;
  }
`;

export const ProjectCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${ProjectCard}:hover & {
    transform: scale(1.1);
  }
`;

export const ProjectCardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  padding: var(--spacing-md);
  
  h3 {
    color: white;
    margin-bottom: var(--spacing-md);
  }
`;

// Press and Media Section
export const PressMediaContainer = styled.div`
  margin-top: var(--spacing-lg);
`;

export const MediaGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const GalleryItem = styled.div`
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

export const GalleryImage = styled.div`
  width: 100%;
  height: 300px;
  overflow: hidden;
  margin-bottom: var(--spacing-sm);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.03);
  }
`;

export const GalleryCaption = styled.div`
  padding: var(--spacing-sm);
  background-color: white;
  
  h3 {
    margin-bottom: var(--spacing-xs);
    font-weight: normal;
    font-size: 1.5rem;
    &::first-letter {
      color: #C13221;
    }
  }
  
  p {
    font-size: 0.9rem;
    color: var(--color-text-secondary);
  }
`;

// Section Détails
export const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 900px;
  margin: 0 auto;
`;

export const DetailsIntro = styled.p`
  text-align: center;
  font-size: 1rem;
  margin-bottom: var(--spacing-lg);
  line-height: 1.6;
  max-width: 700px;
`;

export const DetailsList = styled.ul`
  width: 100%;
  list-style: none;
  padding: 0;
  margin: var(--spacing-md) 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const DetailsItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  img {
    width: 100%;
    height: auto;
    object-fit: cover;
    border-radius: 2px;
  }
`;

export const DetailsText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
`;

// Keeping old Press styles for backward compatibility
export const PressItem = styled.div`
  display: none;
`;

export const PressLogo = styled.div`
  display: none;
`;

export const PressContent = styled.div`
  display: none;
`;

// Stats Section
export const StatsSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  gap: var(--spacing-md);
  padding: 0;
  margin-top: -20px;
`;

export const StatItem = styled.div`
  flex: 1;
  min-width: 200px;
  text-align: center;
  padding: var(--spacing-md);
`;

export const StatIcon = styled.div`
  margin-bottom: var(--spacing-sm);
  display: none; // Cacher les icônes
  
  svg {
    width: 40px;
    height: 40px;
    color: var(--color-text);
    opacity: 0.6;
  }
`;

export const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-xs);
  color: var(--color-accent);
`;

export const StatLabel = styled.div`
  font-size: 0.95rem;
  color: var(--color-dark-gray);
  font-weight: var(--font-weight-light);
`;

// Testimonials Section
export const TestimonialsContainer = styled.div`
  margin-top: var(--spacing-lg);
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-xl);
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

export const Testimonial = styled.div`
  text-align: left;
  height: 100%;
  padding: var(--spacing-lg);
  background-color: var(--color-light-gray);
  transition: transform 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
  }
  
  &::before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 4rem;
    font-family: var(--font-primary);
    color: rgba(0, 0, 0, 0.05);
    line-height: 1;
  }
`;

export const TestimonialText = styled.blockquote`
  font-size: 1rem;
  font-style: italic;
  margin-bottom: var(--spacing-md);
  line-height: 1.7;
  font-family: var(--font-primary);
  position: relative;
  font-weight: 300;
  padding-left: 0;
  margin-left: 0;
  color: var(--color-dark-gray);
`;

export const TestimonialAuthor = styled.div`
  font-weight: 500;
  margin-top: var(--spacing-md);
  font-size: 0.95rem;
`;

export const TestimonialRole = styled.p`
  font-size: 0.9rem;
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-weight: 300;
`;

// About Section
export const AboutSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md) 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--spacing-md);
  }
`;

export const AboutImageContainer = styled.div`
  flex: 1;
  max-width: 40%;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 20px;
    left: 20px;
    right: -20px;
    bottom: -20px;
    border: 1px solid var(--color-medium-gray);
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    margin-bottom: var(--spacing-md);
  }
`;

export const AboutImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
  border-radius: 2px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
`;

export const AboutContent = styled.div`
  flex: 1;
  text-align: left;
`;

export const AboutSubheading = styled.h3`
  margin-bottom: var(--spacing-sm);
  font-weight: var(--font-weight-light);
  color: #666;
`;

export const AboutText = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: var(--spacing-sm);
`;

// CTA Section
export const CtaContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

export const CtaTitle = styled.h2`
  margin-bottom: var(--spacing-sm);
`;

export const CtaText = styled.p`
  margin-bottom: var(--spacing-md);
  font-size: 1.1rem;
`;

// Styled components pour la section défilante de logos
export const LogoSection = styled.div`
  background-color: white;
  padding: var(--spacing-md) 0;
  position: relative;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  max-width: var(--container-width);
  margin: 0 auto;
`;

export const LogosSectionTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: var(--font-weight-light);
  color: var(--color-text);
  text-align: center;
  margin-bottom: var(--spacing-sm);
`;

export const LogoSliderContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  padding: var(--spacing-sm) 0;
`;

export const LogoSliderTrack = styled.div`
  display: flex;
  animation: slideLogos 20s linear infinite;
  
  @keyframes slideLogos {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(calc(-170px * 6));
    }
  }
`;

export const LogoItem = styled.div`
  padding: 0 var(--spacing-sm);
  flex-shrink: 0;
  width: 170px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
`;

export const LogoImage = styled.img`
  max-height: 60px;
  max-width: 150px;
  filter: grayscale(100%);
  opacity: 0.8;
  transition: opacity 0.3s ease, transform 0.3s ease, filter 0.3s ease;
  object-fit: contain;
  
  &:hover {
    transform: scale(1.05);
    opacity: 1;
  }
`;

// Styles pour la section des types de clients
export const ClientTypesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  margin-top: var(--spacing-lg);
  padding: 0 var(--spacing-lg);
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-md);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-md);
  }
`;

export const ClientTypeCard = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ClientTypeImage = styled.img`
  width: 100%;
  aspect-ratio: 1/1;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.03);
  }
`;

export const ClientTypeOverlay = styled.div`
  padding: var(--spacing-sm) 0;
  margin-top: var(--spacing-sm);
`;

export const ClientTypeTitle = styled.h3`
  font-size: 1rem;
  font-weight: var(--font-weight-regular);
  letter-spacing: 2px;
  text-align: center;
  color: var(--color-text);
  text-transform: uppercase;
  margin: 0;
`;

export const ClientTypeDescription = styled.p`
  font-size: 0.9rem;
  opacity: 0.9;
`;
